import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  usePlatformSettings,
  usePlatformSettingsHistory,
  usePlatformSettingsVersion,
  useRestorePlatformSettings,
  useUpdatePlatformSettings,
} from '../hooks/api/usePlatformSettings';

function SettingsForm({ settings, onSave, isSaving, saveMessage, onSaveMessageChange }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    gracePeriodDays: 5,
    defaultTrialDays: 14,
    readOnlyOnPastDue: true,
  });

  useEffect(() => {
    if (settings) {
      setForm({
        gracePeriodDays: settings.gracePeriodDays,
        defaultTrialDays: settings.defaultTrialDays,
        readOnlyOnPastDue: settings.readOnlyOnPastDue,
      });
    }
  }, [settings]);

  const handleSubmit = () => {
    onSave({
      gracePeriodDays: Number(form.gracePeriodDays),
      defaultTrialDays: Number(form.defaultTrialDays),
      readOnlyOnPastDue: form.readOnlyOnPastDue,
      message: saveMessage.trim() || undefined,
    });
  };

  return (
    <Stack spacing={2} component={Paper} sx={{ p: { xs: 2, sm: 3 } }}>
      <TextField
        label={t('settings.gracePeriodDays')}
        type="number"
        value={form.gracePeriodDays}
        onChange={(event) => setForm((current) => ({ ...current, gracePeriodDays: event.target.value }))}
        inputProps={{ min: 0 }}
        fullWidth
        helperText={t('settings.gracePeriodDaysHelp')}
      />
      <TextField
        label={t('settings.defaultTrialDays')}
        type="number"
        value={form.defaultTrialDays}
        onChange={(event) => setForm((current) => ({ ...current, defaultTrialDays: event.target.value }))}
        inputProps={{ min: 0 }}
        fullWidth
        helperText={t('settings.defaultTrialDaysHelp')}
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.readOnlyOnPastDue}
            onChange={(event) => setForm((current) => ({ ...current, readOnlyOnPastDue: event.target.checked }))}
          />
        }
        label={t('settings.readOnlyOnPastDue')}
      />
      <Typography variant="caption" color="text.secondary">
        {t('settings.readOnlyOnPastDueHelp')}
      </Typography>
      <TextField
        label={t('settings.versionMessage')}
        value={saveMessage}
        onChange={(event) => onSaveMessageChange(event.target.value)}
        fullWidth
        placeholder={t('settings.versionMessagePlaceholder')}
      />
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={isSaving}
        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
      >
        {isSaving ? t('common.loading') : t('settings.save')}
      </Button>
    </Stack>
  );
}

function SettingsHistoryPanel({ history = [], status, onRestore, restoringHash }) {
  const { t } = useTranslation();

  if (!history.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        {t('settings.historyEmpty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {status?.hash && (
        <Typography variant="caption" color="text.secondary">
          {t('settings.currentVersion', { hash: status.hash, branch: status.branch })}
        </Typography>
      )}
      {history.map((entry) => (
        <Box
          key={entry.hash}
          sx={{
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold">
              {entry.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.author} · {entry.date} · {entry.hash}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onRestore(entry)}
            disabled={restoringHash === entry.hash}
            sx={{ flexShrink: 0 }}
          >
            {restoringHash === entry.hash ? t('common.loading') : t('settings.restore')}
          </Button>
        </Box>
      ))}
    </Stack>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [saveMessage, setSaveMessage] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreMessage, setRestoreMessage] = useState('');

  const { data: settings, isLoading, error } = usePlatformSettings();
  const { data: historyData, isLoading: historyLoading } = usePlatformSettingsHistory();
  const updateSettings = useUpdatePlatformSettings();
  const restoreSettings = useRestorePlatformSettings();
  const { data: previewVersion } = usePlatformSettingsVersion(restoreTarget?.hash, {
    enabled: Boolean(restoreTarget),
  });

  const history = useMemo(() => historyData?.history ?? [], [historyData]);
  const status = historyData?.status;

  const handleSave = async (payload) => {
    setFeedback({ type: '', message: '' });
    try {
      await updateSettings.mutateAsync(payload);
      setSaveMessage('');
      setFeedback({ type: 'success', message: t('settings.saveSuccess') });
    } catch (saveError) {
      setFeedback({ type: 'error', message: saveError.message || t('common.error') });
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setFeedback({ type: '', message: '' });
    try {
      await restoreSettings.mutateAsync({
        hash: restoreTarget.hash,
        message: restoreMessage.trim() || undefined,
      });
      setRestoreTarget(null);
      setRestoreMessage('');
      setFeedback({ type: 'success', message: t('settings.restoreSuccess') });
    } catch (restoreError) {
      setFeedback({ type: 'error', message: restoreError.message || t('common.error') });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message || t('common.error')}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          {t('settings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('settings.subtitle')}
        </Typography>
      </Box>

      {feedback.message && (
        <Alert severity={feedback.type === 'success' ? 'success' : 'error'}>{feedback.message}</Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label={t('settings.tabs.current')} />
        <Tab label={t('settings.tabs.history')} />
      </Tabs>

      {tab === 0 && (
        <SettingsForm
          settings={settings}
          onSave={handleSave}
          isSaving={updateSettings.isPending}
          saveMessage={saveMessage}
          onSaveMessageChange={setSaveMessage}
        />
      )}

      {tab === 1 && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <SettingsHistoryPanel
              history={history}
              status={status}
              onRestore={setRestoreTarget}
              restoringHash={restoreSettings.isPending ? restoreTarget?.hash : null}
            />
          )}
        </Paper>
      )}

      <Dialog
        open={Boolean(restoreTarget)}
        onClose={() => !restoreSettings.isPending && setRestoreTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('settings.restoreTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('settings.restoreConfirm', { hash: restoreTarget?.hash, message: restoreTarget?.message })}
            </Typography>
            {previewVersion?.settings && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {t('settings.restorePreview')}
                </Typography>
                <Typography variant="body2">
                  {t('settings.gracePeriodDays')}: {previewVersion.settings.gracePeriodDays}
                </Typography>
                <Typography variant="body2">
                  {t('settings.defaultTrialDays')}: {previewVersion.settings.defaultTrialDays}
                </Typography>
                <Typography variant="body2">
                  {t('settings.readOnlyOnPastDue')}:{' '}
                  {previewVersion.settings.readOnlyOnPastDue ? t('settings.yes') : t('settings.no')}
                </Typography>
              </Box>
            )}
            <TextField
              label={t('settings.versionMessage')}
              value={restoreMessage}
              onChange={(event) => setRestoreMessage(event.target.value)}
              fullWidth
              placeholder={t('settings.restoreMessagePlaceholder')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button
            onClick={() => setRestoreTarget(null)}
            disabled={restoreSettings.isPending}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('settings.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleRestore}
            disabled={restoreSettings.isPending}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {restoreSettings.isPending ? t('common.loading') : t('settings.restore')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
